import type { KnowledgeTree, TreePage } from './types';

/** The tree as the UI walks it: live pages by parent, in sibling order. */
export interface TreeIndex {
  root: TreePage;
  byId: Map<string, TreePage>;
  /** Live children of a page, the root's children under the root's id. */
  children: (id: string) => TreePage[];
  /** Archived pages, newest first. */
  archived: TreePage[];
  /** The page's ancestors from the root down, not including the page itself. */
  ancestors: (id: string) => TreePage[];
  /** The page and every live page below it. */
  subtree: (id: string) => Set<string>;
  /** Every live page, depth first from the root, with its depth. */
  flat: () => { page: TreePage; depth: number }[];
}

const bySiblingOrder = (a: TreePage, b: TreePage) =>
  a.position - b.position || a.title.localeCompare(b.title) || a.id.localeCompare(b.id);

export function indexTree(tree: KnowledgeTree): TreeIndex {
  const byId = new Map(tree.pages.map((page) => [page.id, page]));
  const root = byId.get(tree.rootId)!;
  const childrenOf = new Map<string, TreePage[]>();
  const archived: TreePage[] = [];
  for (const page of tree.pages) {
    if (page.isRoot) continue;
    if (page.archivedAt) {
      archived.push(page);
      continue;
    }
    // A parent the tree does not list (or an archived one) leaves the page under the root.
    const parent = page.parentId && byId.get(page.parentId);
    const key = parent && !parent.archivedAt ? parent.id : root.id;
    const siblings = childrenOf.get(key) ?? [];
    siblings.push(page);
    childrenOf.set(key, siblings);
  }
  for (const siblings of childrenOf.values()) siblings.sort(bySiblingOrder);
  archived.sort((a, b) => (b.archivedAt ?? '').localeCompare(a.archivedAt ?? ''));
  const children = (id: string) => childrenOf.get(id) ?? [];
  const parentOf = (page: TreePage) => {
    if (page.isRoot) return null;
    const parent = page.parentId ? byId.get(page.parentId) : null;
    return parent && !parent.archivedAt ? parent : root;
  };
  return {
    root,
    byId,
    children,
    archived,
    ancestors(id) {
      const path: TreePage[] = [];
      const seen = new Set<string>();
      let current = byId.get(id);
      while (current && !seen.has(current.id)) {
        seen.add(current.id);
        const parent = parentOf(current);
        if (!parent) break;
        path.unshift(parent);
        current = parent;
      }
      return path;
    },
    subtree(id) {
      const found = new Set<string>();
      const walk = (pageId: string) => {
        if (found.has(pageId)) return;
        found.add(pageId);
        for (const child of children(pageId)) walk(child.id);
      };
      walk(id);
      return found;
    },
    flat() {
      const rows: { page: TreePage; depth: number }[] = [];
      const seen = new Set<string>();
      const walk = (page: TreePage, depth: number) => {
        if (seen.has(page.id)) return;
        seen.add(page.id);
        rows.push({ page, depth });
        for (const child of children(page.id)) walk(child, depth + 1);
      };
      walk(root, 0);
      return rows;
    },
  };
}

/** Pages whose title contains `needle`, compared without case. */
export function matchTitle(title: string, needle: string) {
  const query = needle.trim().toLocaleLowerCase();
  return !query || title.toLocaleLowerCase().includes(query);
}
